import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FastCheckinParkingSessionInfoDTO, ParkingProcessTariffInfoDTO, ParkingQuotaInfoDTO } from 'src/app/base/models/models.swagger.generated';
import { PriceUtils } from 'src/app/base/utils/price-utils';
import { FastCheckinTimeSettingsComponent } from '../../fast-checkin/shared/fast-checkin-time-settings/fast-checkin-time-settings.component';
import { MaxParkingDurationHelper } from '../../parking/helpers/max-parking-duration.helper';
import { DateTime } from 'luxon';
import { ParkingTimesHelper } from '../../parking/helpers/parking-times.helper';
import { FastCheckinSessionService } from '../fast-checkin-session.service';

@Component({
  selector: 'app-fast-checkin-session-active',
  templateUrl: './fast-checkin-session-active.component.html',
  styleUrls: ['./fast-checkin-session-active.component.scss'],
  imports: [TranslateModule, FastCheckinTimeSettingsComponent]
})
export class FastCheckinSessionActiveComponent implements OnInit {
  @Input() public sessionInfo: FastCheckinParkingSessionInfoDTO | null = null;
  @Input() public extensionPriceWithCurrency: string = '';
  @Input() public isExtensionPriceLoading: boolean = false;
  @Output() public checkOut: EventEmitter<void> = new EventEmitter<void>();
  @Output() public extendSession: EventEmitter<number> = new EventEmitter<number>();
  @Output() public extensionDurationChanged: EventEmitter<number> = new EventEmitter<number>();

  public showExtensionSettings: boolean = false;
  public extensionDurationMinutes: number = 30;
  public extensionStartTime: Date = new Date();

  private parkingQuotaInfo: ParkingQuotaInfoDTO | null = null;
  private maxParkingDurationInMinutes: number | null = null;
  private maxEndTime: DateTime | null = null;
  private hasTariffs: boolean = false;

  public get maxExtensionDurationMinutes(): number | null {
    return MaxParkingDurationHelper.calculateRemainingExtensionDurationInMinutes(
      this.maxParkingDurationInMinutes,
      this.sessionInfo?.maxIntendedParkingDurationMinutes
    );
  }

  public get hasAvailableExtensionDuration(): boolean {
    return this.maxExtensionDurationMinutes == null || this.maxExtensionDurationMinutes > 0;
  }

  public get sessionPriceWithCurrency(): string {
    if (this.sessionInfo) {
      return PriceUtils.getPriceWithCurrency(this.sessionInfo.totalWithVat, this.sessionInfo.currency);
    }
    return '';
  }

  constructor(private fastCheckinSessionService: FastCheckinSessionService) { }

  ngOnInit() {
    this.refreshParkingTimesFromSessionInfo();
  }

  public onOpenExtensionSettings() {
    this.loadRemainingQuotaIfRequired();

    this.extensionDurationMinutes = this.getInitialExtensionDurationMinutes();
    this.extensionStartTime = this.sessionInfo?.endDateTime ? this.sessionInfo.endDateTime.toJSDate() : new Date();
    this.showExtensionSettings = true;
    if (!this.hasAvailableExtensionDuration) {
      return;
    }
    this.extensionDurationChanged.emit(this.extensionDurationMinutes);
  }

  public onCancelExtensionSettings() {
    this.showExtensionSettings = false;
  }

  public onExtensionDurationChanged(durationMinutes: number) {
    const maxExtensionDurationMinutes = this.maxExtensionDurationMinutes;
    if (maxExtensionDurationMinutes != null && durationMinutes > maxExtensionDurationMinutes) {
      this.extensionDurationMinutes = maxExtensionDurationMinutes;
    } else {
      this.extensionDurationMinutes = durationMinutes;
    }

    this.extensionDurationChanged.emit(this.extensionDurationMinutes);
  }

  public onApplyExtensionSettings() {
    if (this.extensionDurationMinutes <= 0) {
      return;
    }

    this.extendSession.emit(this.extensionDurationMinutes);
    this.showExtensionSettings = false;
  }

  public formatDuration(minutes: number | null): string {
    if (minutes == null) {
      return '-';
    }

    if (minutes <= 0) {
      return '0 min';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} min`;
    }

    if (remainingMinutes === 0) {
      return `${hours} h`;
    }

    return `${hours} h ${remainingMinutes} min`;
  }

  private getInitialExtensionDurationMinutes(): number {
    const defaultExtensionDurationMinutes = 60;
    const maxExtensionDurationMinutes = this.maxExtensionDurationMinutes;
    if (maxExtensionDurationMinutes == null) {
      return defaultExtensionDurationMinutes;
    }

    return Math.min(defaultExtensionDurationMinutes, maxExtensionDurationMinutes);
  }

  private loadRemainingQuotaIfRequired(): void {
    const subscription = this.sessionInfo?.parkingSubscriptionInfo;
    if (!subscription?.parkingQuotaHours) {
      this.parkingQuotaInfo = null;
      this.refreshParkingTimesFromSessionInfo();
      return;
    }

    const selectedSubscriptionId = subscription.id;
    const zoneId = this.sessionInfo?.zone?.id;
    const controlPlateNumber = this.sessionInfo?.controlPlateNumber;
    if (!selectedSubscriptionId || !zoneId || !controlPlateNumber) {
      this.parkingQuotaInfo = null;
      this.refreshParkingTimesFromSessionInfo();
      return;
    }

    this.fastCheckinSessionService.getForeignParkingFastCheckinQuotaInfo(zoneId, selectedSubscriptionId, controlPlateNumber)
      .subscribe((quotaInfo) => {
        this.parkingQuotaInfo = quotaInfo;
        if (this.parkingQuotaInfo?.remainingParkingQuotaInMinutes != null && this.parkingQuotaInfo.remainingParkingQuotaInMinutes < 0) {
          this.parkingQuotaInfo.remainingParkingQuotaInMinutes = 0;
        }

        this.refreshParkingTimesFromSessionInfo();

        const maxExtensionDurationMinutes = this.maxExtensionDurationMinutes;
        if (maxExtensionDurationMinutes != null && this.extensionDurationMinutes > maxExtensionDurationMinutes) {
          this.extensionDurationMinutes = maxExtensionDurationMinutes;
        }
      });
  }

  private refreshParkingTimesFromSessionInfo(): void {
    const subscription = this.sessionInfo?.parkingSubscriptionInfo;
    const tariffs = subscription?.tariffs;
    const maxParkingDurationInMinutes = MaxParkingDurationHelper.resolveEffectiveMaxParkingDurationInMinutes(
      subscription?.maxParkingDurationInMin,
      this.parkingQuotaInfo?.remainingParkingQuotaInMinutes
    );

    this.updateParkingTimes(tariffs, null, maxParkingDurationInMinutes, null);
  }

  private updateParkingTimes(tariffs: ParkingProcessTariffInfoDTO[] | null | undefined,
                             tariffsMaxValidityTime: DateTime | null | undefined,
                             subscriptionsMaxParkingDuration: number | null | undefined,
                             fallbackParkingTariffs: ParkingProcessTariffInfoDTO[] | null | undefined): void {
    const parkingTimesState = ParkingTimesHelper.calculateParkingTimesState(
      this.sessionInfo?.startDateTime?.toUTC() ?? DateTime.utc(),
      tariffs,
      tariffsMaxValidityTime,
      subscriptionsMaxParkingDuration ?? 0,
      fallbackParkingTariffs,
      {
        maxEndTime: this.maxEndTime,
        hasTariffs: this.hasTariffs,
      }
    );

    this.maxParkingDurationInMinutes = parkingTimesState.isParkingUnlimited ? null : parkingTimesState.maxParkingDuration;
    this.maxEndTime = parkingTimesState.maxEndTime;
    this.hasTariffs = parkingTimesState.hasTariffs;
  }

}
